import { useFirebase } from "containers/FirebaseContext";
import { collection, deleteDoc, doc, doc as firestoreDoc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import * as Sentry from '@sentry/react';

export const useShareNotification = () => {
    const { firestore } = useFirebase();
    const createShareNotification = async (shareNotificationData, shareId, share_id = null) => {
        try {
            const generated_session_id = shareId + shareNotificationData?.id;
            const docRef = firestoreDoc(firestore, "share_notifications", generated_session_id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap?.data();
                const existingNotificationList = Array.from(new Set(data?.notification_list || []));
                const existingIds = new Set(existingNotificationList);
                const newNotificationList = (shareNotificationData?.notification_list || []).filter(id => !existingIds.has(id));
                const mergedNotificationList = [...existingNotificationList, ...newNotificationList];
                const newData = {
                    ...shareNotificationData,
                    notification_list: mergedNotificationList,
                }
                if (share_id) {
                    const userSeen = data?.userSeen || [];
                    const filteredUserSeen = userSeen.filter(id => id !== share_id);
                    newData.userSeen = filteredUserSeen;
                }
                await updateDoc(docRef, newData)
            } else {
                const deduplicatedData = {
                    ...shareNotificationData,
                    notification_list: Array.from(new Set(shareNotificationData?.notification_list || []))
                };
                await setDoc(docRef, deduplicatedData)
            }
            return;
        } catch (error) {
            Sentry.captureException(`658 Error in createShareNotification: ${error}`);
            console.log(error, "error")
        }
    }

    const listenToSentenceData = (keyID, callback) => {
        if (!keyID) {
            return () => { };
        }
        const collectionRef = collection(firestore, "sentence_starter");
        const q = query(collectionRef, where("id", "==", keyID));
    
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const sharableData = {};
            querySnapshot.forEach((doc) => {
                sharableData[doc.id] = doc.data();
            });
            callback(sharableData);
        }, (error) => {
            Sentry.captureException(`659 Error listening to session changes: ${error}`);
            console.error("Error listening to session changes:", error);
        });
    
        return unsubscribe;
    };    

    const createSentenceStarter = async (sentenceData,generated_id) => {
        try {
            const docRef = firestoreDoc(firestore, "sentence_starter", generated_id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                await updateDoc(docRef, sentenceData);
            } else {
                await setDoc(docRef, sentenceData);
            }
            return;
        } catch (error) {
            Sentry.captureException(`660 Error in createSentenceStarter: ${error}`);
            console.log(error, "error")
        }
    }

    const deleteSentenceData = async (docId) => {
        try {
            const docRef = doc(firestore, "sentence_starter", docId);
            await deleteDoc(docRef);
            console.log(`Document with ID ${docId} successfully deleted!`);
        } catch (error) {
            console.error("Error deleting document: ", error);
        }
    };


    const updateShareNotification = async (docId, userId) => {
        try {
            const notificationsRef = collection(firestore, "share_notifications");
            const docRef = firestoreDoc(notificationsRef, docId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                const existingUser = data.userSeen && data.userSeen.includes(userId);
                if (!existingUser) {
                    await updateDoc(docRef, {
                        userSeen: [...(data.userSeen || []), userId]
                    });
                }
            }
        } catch (error) {
            Sentry.captureException(`661 Error in updateShareNotification: ${error}`);
        }
    }

    const updateShareNotificationsBatch = async (docIds, userId) => {
        try {
            if (!docIds || docIds.length === 0 || !userId) {
                return;
            }

            const notificationsRef = collection(firestore, "share_notifications");
            const docRefs = docIds.map(docId => firestoreDoc(notificationsRef, docId));
            const docSnaps = await Promise.all(docRefs.map(docRef => getDoc(docRef)));

            const batch = writeBatch(firestore);
            let hasUpdates = false;

            docSnaps.forEach((docSnap, index) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const existingUser = data.userSeen && data.userSeen.includes(userId);
                    if (!existingUser) {
                        const docRef = docRefs[index];
                        batch.update(docRef, {
                            userSeen: [...(data.userSeen || []), userId]
                        });
                        hasUpdates = true;
                    }
                }
            });

            if (hasUpdates) {
                await batch.commit();
            }
        } catch (error) {
            Sentry.captureException(`662 Error in updateShareNotificationsBatch: ${error}`);
        }
    }

    return { createShareNotification, updateShareNotification, updateShareNotificationsBatch, createSentenceStarter, deleteSentenceData, listenToSentenceData }
}